import { useEffect, useState } from 'react';
import { formatTranslation } from 'intl';
import { lastEdit } from 'shared/domain/lastEdit';
import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';
import { HashMap } from 'shared/types/commonView';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { useProjectTimezone } from 'store/project';
import { CommonTranslationKeys } from 'intl/keys';

export function useLastEditString(
  intl: IntlShape,
  modifiedAt: DateTime | string,
  modifiedBy: string | undefined,
  isExpanded: boolean,
  visibleFields: HashMap<boolean>
): string {
  const timezone = useProjectTimezone();
  if (typeof modifiedAt === 'string') {
    modifiedAt = DateTime.fromISO(modifiedAt, {
      zone: timezone,
    });
  }
  const [lastEditString, setLastEditString] = useState(
    getLastEditString(
      intl,
      modifiedAt,
      modifiedBy,
      isExpanded,
      visibleFields,
      timezone
    )
  );

  useEffect(() => {
    const set = (): void =>
      setLastEditString(
        getLastEditString(
          intl,
          modifiedAt,
          modifiedBy,
          isExpanded,
          visibleFields,
          timezone
        )
      );
    const interval = setInterval(set, 60000);
    set();
    return () => {
      clearInterval(interval);
    };
  }, [intl, modifiedAt, modifiedBy, isExpanded, visibleFields, timezone]);

  return lastEditString;
}

function getLastEditString(
  intl: IntlShape,
  modifiedAt: DateTime | string,
  modifiedBy: string | undefined,
  isExpanded: boolean,
  visibleFields: HashMap<boolean>,
  timezone: string
): string {
  if (!visibleFields[IssueFieldNames.modificationDate]) {
    return '';
  }
  if (typeof modifiedAt === 'string') {
    modifiedAt = DateTime.fromISO(modifiedAt, {
      zone: timezone,
    });
  }
  const editedAt = lastEdit(
    DateTime.now().setZone(timezone),
    modifiedAt.setZone(timezone),
    intl.locale
  );
  const userLabel = modifiedBy;

  if (!editedAt) {
    return '';
  }

  const skipUser =
    !visibleFields[IssueFieldNames.modifiedBy] ||
    !userLabel ||
    !isExpanded;

  if (skipUser && editedAt.now) {
    return formatTranslation(intl, CommonTranslationKeys.modifiedAtRelatedTimeJustNow,);
  }

  if (skipUser) {
    return formatTranslation(intl, CommonTranslationKeys.modifiedAtRelatedTime , { periodText: editedAt.period }
    );
  }

  if (!skipUser && editedAt.now) {
    return formatTranslation(intl, CommonTranslationKeys.modifiedAtRelatedTimeWithUserJustNow , { userLabel }
    );
  }

  return formatTranslation(intl, CommonTranslationKeys.modifiedAtRelatedTimeWithUser , { userLabel, periodText: editedAt.period }
  );
}
